import { Alert, Avatar, Button, Card, Col, Input, Row, Skeleton, Typography } from "antd";
import axios from "axios";
import useViewport from "components/UseViewport/UseViewport";
import { getWeek } from "helpers/functions";
import React, { useCallback, useEffect, useState } from "react";
import "react-day-picker/dist/style.css";
import { BookingSeason, NO_AVAILABLE_SEASON, NoAvailableSeason, UNKNOWN_ERROR, UnknownError } from "../interface";
import Calendar from "./Calendar";
import "./Calendar.css";

const { Text, Title } = Typography;
const { TextArea } = Input;

interface Props {
  season: BookingSeason | NoAvailableSeason | UnknownError | undefined;
  setPrice: (price: string) => void;
  setSelectedDate: (newDate: Date | undefined) => void;
  setDescription: (desc: string | undefined) => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  selectedDate: Date | undefined;
  description: string | undefined;
}

const Booking: React.FC<Props> = ({
  season,
  setPrice,
  goToNextStep,
  goToPreviousStep,
  selectedDate,
  setSelectedDate,
  setDescription,
  description,
}) => {
  const { width } = useViewport();
  const breakpoint = 1310;

  // Used when making a booking request
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const calculatePrice = () => {
    // New price model for fall 2023
    if (!selectedDate) return;
    const weekday = selectedDate.getDay();
    const weeknum = getWeek(selectedDate);

    let price = 12000; // Basis
    if ((weeknum >= 39 && weeknum <= 42) || (weeknum >= 3 && weeknum <= 13)) {
      price = 24000; // Premium
      if (weekday === 4) {
        price += 4800;
      }
    }
    if (weeknum >= 35 && weeknum <= 38) {
      price = 32000; // Exclusive
      if (weekday === 4) {
        price += 6400;
      }
    }
    setPrice(`${price.toLocaleString("no")} kr ekskl. mva`);
    return `${price.toLocaleString("no")} kr ekskl. mva`;
  };

  const openTime = typeof season === "object" ? new Date(season.booking_open) : undefined;
  const [secondsLeft, setSecondsLeft] = useState(
    openTime ? Math.floor((openTime.getTime() - new Date().getTime()) / 1000) : Number("Infinity")
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setSecondsLeft(curr => curr - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const hoursLeft = Math.floor(secondsLeft / 3600);
  const minutesLeft = Math.floor((secondsLeft % 3600) / 60);

  const opensToday = openTime?.getDate() === new Date().getDate() && openTime.getMonth() === new Date().getMonth();
  const fullDate =
    openTime &&
    new Intl.DateTimeFormat("no-NO", {
      weekday: "long",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
    }).format(openTime);
  const time = openTime && new Intl.DateTimeFormat("no-NO", { hour: "numeric", minute: "numeric" }).format(openTime);

  const wasOpenOnPageLoad = typeof season === "object" ? season.is_open : undefined;
  const isOpenNow = secondsLeft <= 0;

  const buttonDisabled = selectedDate === undefined || !isOpenNow;

  const placeBooking = useCallback(() => {
    setLoading(true);
    axios
      .post("/booking-seasons/book/", { date: selectedDate?.toISOString().split("T")[0], comment: description })
      .then(() => {
        goToNextStep();
      })
      .catch(e => {
        if (
          "response" in e &&
          "data" in e.response &&
          typeof e.response.data === "object" &&
          "error" in e.response.data
        ) {
          setError(String(e.response.data.error));
        } else {
          setError("En ukjent feil oppstod, vennligst prøv igjen senere eller ta kontakt med Bindeleddet");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [description, goToNextStep, selectedDate]);

  useEffect(
    function dryRun() {
      // Do a dry-run of a booking to see if there is anything that prevents the company from booking
      axios
        .post("/booking-seasons/book/", { dry_run: true, date: selectedDate?.toISOString().split("T")[0] })
        .then(() => {
          setError(undefined);
        })
        .catch(e => {
          if ("response" in e && "data" in e.response && typeof e.response.data === "object") {
            setError(Object.values(e.response.data).join("\n"));
          } else {
            setError("En ukjent feil oppstod, vennligst prøv igjen senere eller ta kontakt med Bindeleddet");
          }
        });
    },
    [selectedDate]
  );

  return (
    <>
      <Row gutter={5} style={{ width: "100%" }}>
        {!wasOpenOnPageLoad && (
          <Col span={24}>
            <Card
              style={{
                width: width < breakpoint ? "520px" : "1045px",
                margin: "5px auto",
              }}
            >
              <Alert
                message={isOpenNow ? "Booking er åpen" : "Booking har ikke åpnet enda"}
                description={
                  <Text>
                    {hoursLeft >= 24
                      ? `Booking åpner ${fullDate}`
                      : hoursLeft >= 1
                      ? `Bookingen åpner om ${hoursLeft} time${
                          hoursLeft === 1 ? "" : "r"
                        } og ${minutesLeft} minutter (${opensToday ? `kl. ${time}` : fullDate})`
                      : minutesLeft >= 1
                      ? `Bookingen åpner om ${minutesLeft} minutter og ${secondsLeft % 60} sekunder`
                      : secondsLeft > 0
                      ? `Bookingen åpner om ${secondsLeft} sekunder`
                      : "Booking er nå åpen"}
                  </Text>
                }
                type={isOpenNow ? "success" : "warning"}
                showIcon
              />
            </Card>
          </Col>
        )}
        {error && (
          <Col span={24}>
            <Card
              style={{
                width: width < breakpoint ? "520px" : "1045px",
                margin: "5px auto",
              }}
            >
              <Alert message="Feil" description={error} type="error" showIcon />
            </Card>
          </Col>
        )}
        <Col span={width < breakpoint ? 24 : 12}>
          <Card
            style={
              width > breakpoint
                ? { width: "520px", margin: "0 0 5px auto" }
                : { width: "520px", margin: "0 auto 5px auto" }
            }
          >
            {season === undefined ? (
              <Skeleton />
            ) : season === NO_AVAILABLE_SEASON ? (
              <Text>Mer informasjon kommer snart</Text>
            ) : season === UNKNOWN_ERROR ? (
              <Text>En ukjent feil oppstod, vennligst prøv igjen senere eller ta kontakt med Bindeleddet</Text>
            ) : (
              <Calendar bookingSeason={season} setSelectedDay={setSelectedDate} loading={loading} />
            )}
          </Card>
        </Col>
        <Col span={width < breakpoint ? 24 : 12}>
          <Row>
            <Card
              style={
                width > breakpoint
                  ? { width: "520px", margin: "0 auto 5px 0" }
                  : { width: "520px", margin: "0 auto 5px auto" }
              }
            >
              <Row style={{ fontSize: "10px" }}>
                <Col span={4} style={{ justifyContent: "center", textAlign: "center" }}>
                  <Avatar
                    shape="circle"
                    style={{ backgroundColor: "var(--primary-color)", border: "3px solid var(--secondary-color)" }}
                  />
                  <Typography>Valgt</Typography>
                </Col>
                <Col span={4} style={{ justifyContent: "center", textAlign: "center" }}>
                  <Avatar shape="circle" style={{ backgroundColor: "#c6ffc6" }} />
                  <Typography>Ledig</Typography>
                </Col>
                <Col span={4} style={{ justifyContent: "center", textAlign: "center" }}>
                  <Avatar shape="circle" style={{ backgroundColor: "#f97c7d" }} />
                  <Typography>Opptatt</Typography>
                </Col>
                <Col span={4} style={{ justifyContent: "center", textAlign: "center" }}>
                  <Avatar shape="circle" style={{ backgroundColor: "#7a1e64" }} />
                  <Typography>KarriereDagene</Typography>
                </Col>
                <Col span={4} style={{ justifyContent: "center", textAlign: "center" }}>
                  <Avatar shape="circle" style={{ backgroundColor: "#1d3b67" }} />
                  <Typography>OsloTuren</Typography>
                </Col>
                <Col span={4} style={{ justifyContent: "center", textAlign: "center" }}>
                  <Avatar shape="circle" style={{ backgroundColor: "#aeaaab" }} />
                  <Typography>Utilgjengelig</Typography>
                </Col>
              </Row>
            </Card>
          </Row>
          <Row>
            <Card
              style={
                width > breakpoint
                  ? { width: "520px", marginLeft: 0, marginRight: "auto" }
                  : { width: "520px", margin: "auto" }
              }
            >
              <Card
                style={{ backgroundColor: "var(--neutral-alt", marginBottom: "10px" }}
                title="Pris for denne datoen:"
              >
                {selectedDate ? <Text strong>{calculatePrice()}</Text> : <Text strong>Velg en dato for å se pris</Text>}
              </Card>
              <Title level={5}>Andre henvendelser</Title>
              <TextArea
                value={description}
                autoSize={{ minRows: 7, maxRows: 7 }}
                onChange={e => setDescription(e.target.value)}
                placeholder="Legg til kommentar til booking. (Frivillig)."
              />
            </Card>
          </Row>
        </Col>
      </Row>
      <Row style={{ justifyContent: "space-between", width: "100%", padding: "10px 0" }}>
        <Button type="default" size="large" onClick={goToPreviousStep}>
          Forrige
        </Button>
        <Button
          disabled={buttonDisabled}
          style={
            buttonDisabled
              ? { backgroundColor: "grey", color: "var(--light)" }
              : { backgroundColor: "#59c09c", color: "var(--light)" }
          }
          size="large"
          onClick={placeBooking}
          loading={loading}
        >
          Book dato
        </Button>
      </Row>
    </>
  );
};

export default Booking;
